import React, { useRef, useState } from 'react';
import {
  Zap,
  MessageSquare,
  BarChart3,
  Code2,
  FileText,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import AnchorDropdownPanel from '../ui/AnchorDropdownPanel';
import { modeAccentClass } from './chatViewTheme';

export const CHAT_MODE_OPTIONS = [
  { id: 'auto', label: 'Auto', description: 'Adapts to your question', icon: Sparkles },
  { id: 'quick', label: 'Quick', description: 'Short, direct answers', icon: Zap },
  {
    id: 'interactive',
    label: 'Guided',
    description: 'Step-by-step workflows',
    icon: MessageSquare,
  },
  { id: 'analyze', label: 'Analyze', description: 'Deep analysis', icon: BarChart3 },
  { id: 'code', label: 'Code', description: 'Search and explain code', icon: Code2 },
  { id: 'report', label: 'Report', description: 'Shareable document output', icon: FileText },
];

/**
 * Compact mode-picker button that shows a dropdown menu.
 * Replaces the tab row previously shown below the input.
 */
export default function ModePicker({ value, onChange }: any) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);
  const menuId = 'vanti-mode-picker';

  const active = CHAT_MODE_OPTIONS.find((m) => m.id === value) || CHAT_MODE_OPTIONS[0];
  const ActiveIcon = active.icon;
  const activeAccent = modeAccentClass[active.id] || modeAccentClass.auto;

  return (
    <div className="relative shrink-0">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-dark-border bg-dark-base px-2.5 py-1.5 text-xs font-medium text-slate-300 transition-all hover:border-dark-border-glow hover:text-white"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        title={`Mode: ${active.label}`}
      >
        <ActiveIcon className={`h-3 w-3 shrink-0 ${activeAccent}`} aria-hidden />
        <span>{active.label}</span>
        <ChevronDown
          className={`h-3 w-3 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      <AnchorDropdownPanel
        id={menuId}
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        ariaLabel="Response mode"
        panelWidth={208}
        placement="above"
        align="start"
      >
        <div className="-m-4 py-1" role="listbox" aria-label="Response mode">
          {CHAT_MODE_OPTIONS.map((m) => {
            const Icon = m.icon;
            const accent = modeAccentClass[m.id] || modeAccentClass.auto;
            const isActive = value === m.id;
            return (
              <button
                key={m.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange(m.id);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-brand-purple/10 text-white'
                    : 'text-slate-300 hover:bg-dark-base hover:text-white'
                }`}
              >
                <Icon
                  className={`h-4 w-4 shrink-0 ${isActive ? accent : 'text-slate-500'}`}
                  aria-hidden
                />
                <div className="min-w-0 text-left">
                  <p className={`text-sm font-medium leading-none ${isActive ? accent : ''}`}>
                    {m.label}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 leading-none">{m.description}</p>
                </div>
                {isActive && (
                  <span
                    className="ml-auto h-1.5 w-1.5 rounded-full bg-brand-purple-light shrink-0"
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>
      </AnchorDropdownPanel>
    </div>
  );
}
